import React, { useMemo, useState } from 'react';
import { BellIcon, LockIcon } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionHeading } from '../SectionHeading';

// STORE CLOSED UNTIL LAUNCH (Ryan, Aug 30). The Shopify catalogue is fully
// drafted, so every /products/<handle> URL 404s and the storefront is empty.
// The tiles below are NON-CLICKABLE previews, not links. Do NOT re-add the
// product anchor or the SHOP ASTROSWAG button until the store is live again.
// See FAR_Store_Disabled_Until_Launch.md for the restore list.
//
// The 13 items here are the curated KEEP line at final Aug-29 pricing, the same
// 13 that flip back to ACTIVE at launch. The Racing and Distressed Metal items
// were unlisted (Ryan, Aug 30) and are deliberately absent.
//
// Product shots are background-removed via Cloudinary AI (far-shop-<key>,
// e_background_removal). TILE CONTRAST (Ryan, Aug 29): several garments are
// black — on the old near-black tile they vanished. The tile now sits several
// steps lighter than the void (indigo plate + white center lift), the category
// glow is much stronger, and each product gets a soft violet rim-glow — so dark
// merch separates while staying inside the brand palette. Don't darken these.

const CLD = (key: string) => `https://res.cloudinary.com/kn1rv77n/image/upload/e_background_removal/f_auto,q_auto,w_320/far-shop-${key}.png`;
type Cat = 'all' | 'apparel' | 'hats' | 'acc';
const TABS: {
  key: Cat;
  label: string;
  dot?: string;
}[] = [{
  key: 'all',
  label: 'ALL'
}, {
  key: 'apparel',
  label: 'APPAREL',
  dot: '#8b5cff'
}, {
  key: 'hats',
  label: 'HATS',
  dot: '#37e5ff'
}, {
  key: 'acc',
  label: 'ACCESSORIES',
  dot: '#c86bff'
}];
const GLOW: Record<string, string> = {
  apparel: 'rgba(139,92,255,0.55)',
  hats: 'rgba(55,229,255,0.42)',
  acc: 'rgba(200,107,255,0.5)'
};

// Handles kept in sync with Shopify so this list can go straight back to links.
const PRODUCTS = [{
  title: 'Basic Hoodie',
  price: '$66',
  handle: 'basic-hoodie',
  cat: 'apparel',
  key: 'hoodie'
}, {
  title: 'Baseball Jersey Black',
  price: '$72',
  handle: 'baseball-jersey-black',
  cat: 'apparel',
  key: 'jerseyb'
}, {
  title: 'Baseball Jersey White',
  price: '$72',
  handle: 'baseball-jersey-white',
  cat: 'apparel',
  key: 'jerseyw'
}, {
  title: 'Faded Tee',
  price: '$40',
  handle: 'oversized-faded-t-shirt',
  cat: 'apparel',
  key: 'fadedtee'
}, {
  title: 'Tie-Dye Tee',
  price: '$40',
  handle: 'oversized-tie-dye-t-shirt',
  cat: 'apparel',
  key: 'tiedyetee'
}, {
  title: 'Rocket Dad Hat',
  price: '$30',
  handle: 'dad-hat',
  cat: 'hats',
  key: 'rocketdad'
}, {
  title: 'FAR Snapback',
  price: '$30',
  handle: 'flat-bill-cap-1',
  cat: 'hats',
  key: 'snapback'
}, {
  title: 'Rocket Beanie',
  price: '$30',
  handle: 'embroidered-beanie',
  cat: 'hats',
  key: 'rocketbeanie'
}, {
  title: 'Vintage Cap',
  price: '$30',
  handle: 'vintage-cap',
  cat: 'hats',
  key: 'vintage'
}, {
  title: 'AOP Bucket Hat',
  price: '$32',
  handle: 'bucket-hat-aop',
  cat: 'hats',
  key: 'aopbucket'
}, {
  title: 'Astronaut Bucket Hat',
  price: '$32',
  handle: 'astronaut-pattern-bucket-hat',
  cat: 'hats',
  key: 'astrobucket'
}, {
  title: 'Space Bandana',
  price: '$25',
  handle: 'all-over-print-bandana',
  cat: 'acc',
  key: 'bandana'
}, {
  title: 'Fanny Pack',
  price: '$44',
  handle: 'fanny-pack',
  cat: 'acc',
  key: 'fanny'
}];
export function AstroSwag() {
  const [cat, setCat] = useState<Cat>('all');
  const shown = useMemo(() => PRODUCTS.filter(p => cat === 'all' || p.cat === cat), [cat]);
  return <section id="astroswag" className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-id="element-82" data-mp="nhxd15k4lwnl">
      <SectionHeading eyebrow="MERCH" title={<>
            Future <span className="text-gradient" data-id="element-84" data-mp="ds4286rt5lke">AstroSwag</span>
          </>} description="Shop's shut while we finish the first drop. Here's what's coming." data-id="element-83" data-mp="8tsrlttov714" />

      <Reveal data-id="element-85" data-mp="11fvexbh9g0m7">
        <div className="mt-7 rounded-2xl bg-gradient-to-r from-[#37e5ff55] via-[#8b5cff55] to-[#c86bff55] p-px shadow-[0_0_44px_rgba(139,92,255,0.14)]" data-id="element-86" data-mp="168ok20gn5ayt">
          <div className="overflow-hidden rounded-[calc(1rem-1px)] bg-[#07051a]/95" data-id="element-87" data-mp="3dt1am3w8nn0n">
            <div className="flex flex-wrap items-center gap-1.5 border-b border-white/10 px-3 py-2.5 sm:px-4 sm:py-3" data-id="element-88" data-mp="39pla2wh1t2io">
              {TABS.map(t => <button key={t.key} type="button" onClick={() => setCat(t.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-tech text-[0.56rem] font-semibold tracking-[0.12em] transition-colors duration-200 sm:text-[0.6rem] ${cat === t.key ? 'border-transparent bg-gradient-to-r from-[#37e5ff] via-[#8b5cff] to-[#c86bff] text-[#050414] shadow-neon' : 'border-white/15 text-white/60 hover:border-violet/50 hover:text-white'}`} data-id="element-89" data-mp="2zsgoaxr4792e">
                  {t.dot && cat !== t.key && <span className="h-1.5 w-1.5 rounded-full" style={{
                background: t.dot
              }} aria-hidden="true" data-id="element-90" data-mp="f8syftug7nwi" />}
                  {t.label}
                </button>)}
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/[0.07] px-3 py-1 font-tech text-[0.52rem] font-semibold tracking-[0.14em] text-cyan/90 sm:text-[0.56rem]" data-id="element-91" data-mp="rmojzsgv1j6p">
                <LockIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-92" data-mp="15kwfrx25uega" />
                OPENS JANUARY 2027
              </span>
            </div>

            <div className="scp-list grid max-h-[47vh] grid-cols-3 gap-2 overflow-y-auto overscroll-contain p-2.5 lg:max-h-[48vh] lg:grid-cols-6 lg:gap-2.5 lg:p-3.5" data-id="element-93" data-mp="1ai4ypzv4umil">
              {shown.map(p => <div key={p.handle} className="flex cursor-default flex-col overflow-hidden rounded-xl border border-white/15 bg-[#0a0720]" data-id="element-94" data-mp="1k8zgos1cizy8">
                  <span className="block aspect-square overflow-hidden" style={{
                background: [`radial-gradient(85% 70% at 50% 36%, rgba(255,255,255,0.13), rgba(255,255,255,0) 68%)`, `radial-gradient(120% 95% at 50% 32%, ${GLOW[p.cat]}, rgba(20,14,52,0) 78%)`, `linear-gradient(180deg, #221a52 0%, #131033 100%)`].join(', ')
              }} data-id="element-95" data-mp="1taqmrxzst8sm">
                    <img src={CLD(p.key)} alt={p.title} loading="lazy" className="h-full w-full object-contain p-2" style={{
                  filter: 'drop-shadow(0 0 14px rgba(164,129,255,0.5)) drop-shadow(0 10px 14px rgba(0,0,0,0.55))'
                }} data-id="element-96" data-mp="cxrutv0303ay" />
                  </span>
                  <span className="px-2 pt-1.5 font-display text-[0.46rem] font-semibold uppercase leading-tight tracking-[0.04em] text-white lg:text-[0.5rem]" data-id="element-97" data-mp="2jng3cd1r3pgs">
                    {p.title}
                  </span>
                  <span className="px-2 pb-1.5 pt-0.5 font-tech text-[0.52rem] text-cyan lg:text-[0.56rem]" data-id="element-98" data-mp="2djjpb0jsi05g">{p.price}</span>
                </div>)}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15} data-id="element-99" data-mp="24pn2rkmpqlpi">
        <div className="mt-5 flex flex-col items-center gap-2 lg:mt-6" data-id="element-100" data-mp="1puqn4mckqw2e">
          <a href="#contact" className="group inline-flex items-center gap-2 rounded-full bg-violet px-7 py-3.5 font-tech text-xs font-semibold tracking-[0.16em] text-white shadow-neon transition-[transform,background-color] duration-200 ease-expo hover:scale-[1.03] hover:bg-violet-soft" data-id="element-101" data-mp="1pwto21f9m9lw">
            <BellIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-102" data-mp="3b83aaj67dusr" />
            TELL ME WHEN IT DROPS
          </a>
          <p className="font-script text-[0.95rem] text-magenta" data-id="element-103" data-mp="3g76ddy2mhqy3">Store opens with the first releases</p>
        </div>
      </Reveal>
    </section>;
}